import React, { createContext, useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { getTheme } from './theme';

export const ColorModeContext = createContext({ toggleColorMode: () => {}, mode: 'light' });
import { AuthProvider, useAuth } from './hooks/useAuth';
import { NotificationProvider } from './hooks/useNotification';
import { ClinicFeaturesProvider } from './hooks/useClinicFeatures';
import Layout from './components/Layout';
import Login from './components/Login';
import Dashboard from './pages/Dashboard';
import Patients from './pages/Patients';
import Doctors from './pages/Doctors';
import Metrics from './pages/Metrics';
import Audit from './pages/Audit';
import Communications from './pages/Communications';
import UsersAdmin from './pages/UsersAdmin';
import Templates from './pages/Templates';
import Home from './pages/Home';
import Register from './pages/Register';
import PatientProfile from './pages/PatientProfile';
import AppointmentSearch from './pages/AppointmentSearch';
import ClinicSettings from './pages/ClinicSettings';
import RecepcionistDashboard from './pages/RecepcionistDashboard';
import Billing from './pages/Billing';
import PrescriptionVerify from './pages/PrescriptionVerify';
import PatientPortal from './pages/PatientPortal';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Algoritmos from './pages/Algoritmos';
import AlgoritmoEditor from './pages/AlgoritmoEditor';
import AlgoritmoViewer from './pages/AlgoritmoViewer';
import Recetas from './pages/Recetas';
import DicomPendientes from './pages/DicomPendientes';
import SuperadminHome from './pages/SuperadminHome';
import CompletarDatos from './pages/CompletarDatos';

const NoClinicMessage = () => {
  const { logout } = useAuth();
  return (
    <Box sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Typography variant="h5" color="error" gutterBottom>No estás asociado a ninguna clínica</Typography>
      <Typography color="text.secondary">Contactá al administrador para que te asigne una clínica.</Typography>
      <Button variant="outlined" color="error" onClick={logout} sx={{ mt: 1 }}>
        Cerrar sesión
      </Button>
    </Box>
  );
};

const AccessDenied = () => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <Typography variant="h5" color="error" gutterBottom>Acceso denegado</Typography>
    <Typography color="text.secondary">No tenés permisos para ver esta sección.</Typography>
  </Box>
);

const ProtectedRoute = ({ children, requireClinic = true, requiredRoles = null }) => {
  const { user, role, loading } = useAuth();

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireClinic && user?.clinic_id == null) return <NoClinicMessage />;
  if (requiredRoles && !requiredRoles.includes(role)) return <AccessDenied />;

  return children;
};

const AppRoutes = () => {
  const { loading, role, doctorId, user } = useAuth();

  const getDefaultRoute = () => {
    // superadmin "actuando como" admin de una clínica (ver useAuth.js
    // actingAsSupport) entra como si fuera admin de esa clínica — mismo
    // default que un admin normal. Solo en "modo plataforma" (sin clínica
    // activa todavía) va a /superadmin.
    if (role === 'superadmin') return '/superadmin';
    if (role === 'admin') return '/metrics';
    if (role === 'medico' && doctorId) return '/dashboard-recepcionista';
    if (role === 'recepcionista') return '/dashboard-recepcionista';
    if (role === 'invitado') return '/algoritmos';
    return '/agenda';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={getDefaultRoute()} replace /> : <Home />} />
      <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} replace /> : <Login setToken={() => window.location.reload()} />} />
      <Route path="/register" element={user ? <Navigate to={getDefaultRoute()} replace /> : <Register setToken={() => window.location.reload()} />} />
      <Route path="/verify" element={<PrescriptionVerify />} />
      <Route path="/portal" element={<PatientPortal />} />
      <Route path="/completar-datos" element={<CompletarDatos />} />
      <Route path="/portal/login" element={<PatientPortal />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Dashboard unificado para medico/recepcionista/admin — pedido
          explícito del usuario 2026-11-02: en consultorios chicos
          cualquiera hace de recepcionista de cualquier médico, así que la
          página de Inicio no debe distinguir rol (misma sala de espera,
          mismo check-in, misma agenda del día — la única diferencia real
          es "Llamar paciente", que WaitingRoom ya muestra para cualquiera
          que pueda gestionar la cola). /dashboard-medico queda como alias
          para no romper links/bookmarks previos a esa URL. */}
      <Route path="/dashboard-medico" element={<Navigate to="/dashboard-recepcionista" replace />} />
      <Route path="/dashboard-recepcionista" element={<ProtectedRoute requiredRoles={['recepcionista', 'admin', 'medico']}><Layout><RecepcionistDashboard /></Layout></ProtectedRoute>} />
      {/* requiredRoles explícito (no null) en las rutas de uso diario —
          antes quedaban abiertas a "cualquier autenticado", lo cual
          incluiría al rol "invitado" (acceso externo, solo Algoritmos) si
          no se excluye acá. El bloqueo real de datos ya lo hace el backend
          (ver auth.py get_current_user), esto es solo para no mostrarle a
          invitado una pantalla que de entrada va a fallar con 403. */}
      <Route path="/agenda" element={<ProtectedRoute requiredRoles={['admin', 'recepcionista', 'medico']}><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/turnos" element={<ProtectedRoute requiredRoles={['admin', 'recepcionista', 'medico']}><Layout><AppointmentSearch /></Layout></ProtectedRoute>} />
      <Route path="/patients" element={<ProtectedRoute requiredRoles={['admin', 'recepcionista', 'medico']}><Layout><Patients /></Layout></ProtectedRoute>} />
      <Route path="/patients/:id" element={<ProtectedRoute requiredRoles={['admin', 'recepcionista', 'medico']}><Layout><PatientProfile /></Layout></ProtectedRoute>} />
      <Route path="/doctors" element={<ProtectedRoute requiredRoles={['admin']}><Layout><Doctors /></Layout></ProtectedRoute>} />
      <Route path="/metrics" element={<ProtectedRoute requiredRoles={['admin', 'recepcionista', 'medico']}><Layout><Metrics /></Layout></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute requiredRoles={['admin', 'medico']}><Layout><Templates /></Layout></ProtectedRoute>} />
      <Route path="/users-admin" element={<ProtectedRoute requiredRoles={['admin']}><Layout><UsersAdmin /></Layout></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute requiredRoles={['admin']}><Layout><Audit /></Layout></ProtectedRoute>} />
      <Route path="/comunicaciones" element={<ProtectedRoute><Layout><Communications /></Layout></ProtectedRoute>} />
      {/* Sub-rutas reales por sección (/settings/general, /settings/modulos,
          etc.) — pedido explícito del usuario, patrón "submenús estilo
          GitHub": poder compartir un link directo a una sección y que el
          botón atrás del navegador funcione entre secciones, no solo
          estado interno de un tab. /settings a secas redirige a /general
          dentro del propio componente (ver ClinicSettings.js). */}
      <Route path="/settings" element={<ProtectedRoute requiredRoles={['admin']}><Layout><ClinicSettings /></Layout></ProtectedRoute>} />
      <Route path="/settings/:section" element={<ProtectedRoute requiredRoles={['admin']}><Layout><ClinicSettings /></Layout></ProtectedRoute>} />
      <Route path="/facturacion" element={<ProtectedRoute requiredRoles={['admin', 'recepcionista']}><Layout><Billing /></Layout></ProtectedRoute>} />
      <Route path="/recetas" element={<ProtectedRoute requiredRoles={['admin', 'medico']}><Layout><Recetas /></Layout></ProtectedRoute>} />
      <Route path="/algoritmos" element={<ProtectedRoute requiredRoles={['admin', 'medico', 'invitado']}><Layout><Algoritmos /></Layout></ProtectedRoute>} />
      <Route path="/algoritmos/:id" element={<ProtectedRoute requiredRoles={['admin', 'medico', 'invitado']}><Layout><AlgoritmoViewer /></Layout></ProtectedRoute>} />
      <Route path="/algoritmos/:id/editar" element={<ProtectedRoute requiredRoles={['admin', 'medico']}><Layout><AlgoritmoEditor /></Layout></ProtectedRoute>} />
      <Route path="/dicom-pendientes" element={<ProtectedRoute requiredRoles={['admin', 'medico']}><Layout><DicomPendientes /></Layout></ProtectedRoute>} />
      {/* requireClinic={false}: un superadmin en "modo plataforma" tiene
          clinic_id=null a propósito (no pertenece a ninguna clínica, ve
          todas) — las demás rutas exigen clinic_id porque son datos de UNA
          clínica puntual. */}
      <Route path="/superadmin" element={<ProtectedRoute requireClinic={false} requiredRoles={['superadmin']}><Layout><SuperadminHome /></Layout></ProtectedRoute>} />

      <Route path="*" element={<Navigate to={user ? getDefaultRoute() : "/"} replace />} />
    </Routes>
  );
};

const App = () => {
  const [mode, setMode] = useState(() => localStorage.getItem('colorMode') || 'light');

  const colorMode = useMemo(() => ({
    mode,
    toggleColorMode: () => {
      setMode(prev => {
        const next = prev === 'light' ? 'dark' : 'light';
        localStorage.setItem('colorMode', next);
        return next;
      });
    },
  }), [mode]);

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <Router>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <NotificationProvider>
              <ClinicFeaturesProvider>
                <AppRoutes />
              </ClinicFeaturesProvider>
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </ColorModeContext.Provider>
  );
};

export default App;
